// frontend/src/pages/DashboardPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { Users, Briefcase, Calendar, Receipt, CreditCard, Send, ChevronDown, ChevronRight, Settings, Scale } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { COPYRIGHT_SHORT_LINE } from '../constants/copyrightNotice';

const buildPdfWorkerSrc = () => {
  const publicUrl = process.env.PUBLIC_URL || '';
  const base = publicUrl ? (window.location.origin + (publicUrl.startsWith('/') ? '' : '/') + publicUrl) : window.location.origin;
  return `${base}/pdf.worker.min.js`.replace(/\/+/g, '/');
};
if (typeof pdfjs?.GlobalWorkerOptions !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = buildPdfWorkerSrc();
}

const QUICK_MENUS = [
  { label: '참여자 관리', path: '/manage', icon: Users },
  { label: '참여자 근무 관리', path: '/work', icon: Briefcase },
  { label: '연차사용등록', path: '/leave-register', icon: Calendar },
  { label: '개별 정산', path: '/leave-settle/individual', icon: Receipt },
  { label: '참여자 급여 관리', path: '/payroll/register', icon: CreditCard },
  { label: '급여 명세표 발송', path: '/payroll/send', icon: Send },
];

const GUIDE_SECTIONS = [
  {
    title: '참여자 관리',
    content: (
      <>
        <p><strong>▸ 참여자 등록</strong> (participant-register)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li><strong>개별 등록:</strong> 연도·사업 선택 후, 신규 참여자의 이름·주민번호·연락처·이메일·은행·계좌·근무장소·읍면동·참여시작일·참여종료일 등을 입력하여 등록합니다.</li>
          <li><strong>엑셀 일괄 등록:</strong> 템플릿에 맞춘 엑셀 파일을 업로드하면 여러 명을 한 번에 등록할 수 있습니다. 암호화된 엑셀은 비밀번호 입력이 필요합니다.</li>
          <li><strong>엑셀 일괄 수정:</strong> 기존 참여자 정보를 엑셀로 수정하여 일괄 반영할 수 있습니다.</li>
          <li><strong>지정 연도 데이터 가져오기:</strong> 다른 연도의 참여자 명단을 현재 연도로 복사하여 가져올 수 있습니다.</li>
        </ul>
        <p><strong>▸ 참여자 관리</strong> (manage)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연도·사업·성명·근무장소·읍면동 등으로 검색하여 참여자 목록을 조회합니다.</li>
          <li>개별 수정으로 인적사항, 참여시작일·종료일, 근무 상태(근무 중/종료) 등을 변경할 수 있습니다.</li>
          <li>일괄 수정 모달에서 선택한 참여자들의 사업·참여기간·담당자·계좌·근무장소·읍면동 등을 한 번에 변경할 수 있습니다.</li>
          <li>엑셀 출력으로 현재 검색 결과를 다운로드할 수 있습니다.</li>
        </ul>
        <p>※ 참여자 정보(이름·주민번호·계좌·이메일 등)는 급여·연차 정산에 직접 사용되므로, 저장 전 실제 정보와 일치하는지 꼭 확인해 주세요.</p>
      </>
    ),
  },
  {
    title: '참여자 근태 관리 (근무·스케줄)',
    content: (
      <>
        <p><strong>▸ 참여자 근무 관리</strong> (work)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연도·월·사업·성명·근무장소·읍면동으로 참여자를 검색하여 월별 근무기록을 조회합니다.</li>
          <li><strong> plannedWorkHours(계획근무시간):</strong> 해당 월의 예정 근무시간을 입력합니다. 만근 기준(사업별 설정)을 충족해야 연차가 발생합니다.</li>
          <li><strong>결근일 관리:</strong> 각 참여자의 결근일을 등록하고, 사유를 선택적으로 입력합니다. 결근이 있으면 해당 월 연차 발생이 되지 않습니다.</li>
          <li><strong>지각·조퇴:</strong> 지각·조퇴 횟수와 상세 내역을 기록할 수 있습니다.</li>
          <li><strong>월 마감:</strong> 해당 월의 근무관리를 마감하면 연차 발생·정산 처리가 진행됩니다. 마감 전에 데이터를 꼼꼼히 확인하세요.</li>
          <li>엑셀 출력, 일괄 결근 등록 기능을 제공합니다.</li>
        </ul>
        <p><strong>▸ 스케줄 표 관리</strong> (schedule)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>참여자별 근무 스케줄(요일·시간)을 등록·수정합니다.</li>
          <li>이전 월 스케줄 적용, 일괄 스케줄 등록으로 작업을 편리하게 할 수 있습니다.</li>
          <li>스케줄 PDF를 출력하여 배포할 수 있습니다.</li>
        </ul>
      </>
    ),
  },
  {
    title: '참여자 연차 관리',
    content: (
      <>
        <p><strong>▸ 참여자 연차 현황</strong> (leave)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연도·사업·월·성명·근무장소·읍면동으로 참여자의 연차 현황을 조회합니다.</li>
          <li>발생연차(누적), 사용연차(누적), 잔여연차, 신청연차(접수중인 연차신청), 연차사용날짜(월), 기지급수당, 정산지급액, 미정산연차 등을 확인할 수 있습니다.</li>
          <li>연차 사용 내역을 모달에서 조회할 수 있으며, 마감 전에는 수정이 제한됩니다.</li>
        </ul>
        <p><strong>▸ 연차 사용 등록</strong> (leave-register)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>참여자가 직접 신청하는 대신, 담당자가 참여자를 선택하여 연차 사용(날짜·시간)을 대신 등록합니다.</li>
          <li>날짜·사용시간·의견을 입력하고, PDF 또는 이미지 첨부파일을 필수로 붙여 신청합니다.</li>
          <li>승인 방식(수동/자동)은 관리자가 [연차 승인 및 취소]에서 설정할 수 있습니다.</li>
        </ul>
        <p><strong>▸ 연차 신청 조회</strong> (leave-inquiry)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연도·사용일자(시작·종료)·사업단·읍면동·성명·상태로 연차 신청 목록을 검색합니다.</li>
          <li>접수중인 신청은 수정·접수취소가 가능하고, 승인된 신청은 승인취소신청(취소 요청)을 할 수 있습니다.</li>
          <li>접수취소는 연차 신청을 철회하고, 취소신청 철회는 승인취소 요청을 취소합니다.</li>
        </ul>
        <p><strong>▸ 연차 승인 및 취소</strong> (leave-approval, 관리자 전용)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>접수중인 연차 신청을 승인·반려하고, 승인된 건을 취소할 수 있습니다.</li>
          <li>승인취소 신청(참여자가 취소 요청한 건)을 검토하여 승인하거나 취소신청 철회 처리합니다.</li>
        </ul>
      </>
    ),
  },
  {
    title: '연차 정산',
    content: (
      <>
        <p><strong>▸ 개별 정산</strong> (leave-settle/individual)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연도·사업·성명·근무장소·읍면동으로 참여자를 검색합니다.</li>
          <li>검색 결과에서 참여자를 선택하면 해당 참여자의 연차 발생·사용·잔여·정산 정보를 상세히 볼 수 있습니다.</li>
          <li>정산지급액(연차 수당)을 입력하고 [정산 처리] 버튼을 누르면 해당 참여자의 정산이 완료됩니다.</li>
        </ul>
        <p><strong>▸ 일괄 정산</strong> (leave-settle/batch)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연도·사업·근무장소·읍면동 조건으로 정산 대상 참여자 목록을 조회합니다.</li>
          <li>여러 참여자를 선택하여 한 번에 정산 처리할 수 있습니다.</li>
          <li>엑셀 출력으로 정산 대상 명단을 다운로드할 수 있습니다.</li>
        </ul>
        <p><strong>▸ 지연 정산 처리</strong> (leave-settle/pending)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>참여 종료 후 아직 정산하지 않은 참여자, 참여 기간을 벗어난 연차·결근이 있는 참여자 등을 확인하고 처리합니다.</li>
        </ul>
        <p><strong>▸ 정산 조회 및 취소</strong> (leave-settle/cancel, 관리자 전용)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>이미 정산 처리된 참여자의 정산을 취소합니다. 선택한 참여자들의 상태가 '활동 중'으로 복귀되고, 연차 정산 내역이 제거됩니다.</li>
        </ul>
      </>
    ),
  },
  {
    title: '급여 검증 및 입력',
    content: (
      <>
        <p><strong>▸ 급여 검증 및 입력</strong> (leave-verify)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>엑셀을 업로드하면 근무·연차·연차수당을 시스템과 비교하고, 엑셀 컬럼 <strong>건강보험·장기요양보험·고용보험</strong>이 있으면 선택 사업단·해당 연·월 <strong>사회보험 입력(본인·개인 부담)</strong>과 비교합니다. 해당 이름의 컬럼이 없으면 그 항목은 모든 행에서 불일치로 처리됩니다.</li>
        </ul>
        <p><strong>▸ 검증 이력 조회</strong> (leave-verify/history)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>검증 실행 이력과 급여자료 출력(개별 사업단) 엑셀 다운로드 이력을 연도·월·사업단·검증 및 출력상태로 조회합니다. 검색 시 정렬은 최근 일시순으로 초기화됩니다.</li>
        </ul>
      </>
    ),
  },
  {
    title: '예산 관리',
    content: (
      <>
        <p><strong>▸ 연차 예산 관리</strong> (budget)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연도별로 전체 사업의 연차 수당 예산을 요약합니다.</li>
          <li>총 예상 예산, 지금까지 집행액, 잔여 예산, 사업별 내역 등을 확인할 수 있습니다.</li>
          <li>참여종료일이 해당 월 말일인 경우 연차 발생으로, 말일이 아니면 미발생으로 추정됩니다.</li>
        </ul>
        <p><strong>▸ 인건비 예산 관리</strong> (labor-budget)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연도·사업별로 인건비(급여) 예산과 집행 현황을 한눈에 확인합니다.</li>
          <li>사업별 예산 배정액, 현재까지 급여 집행액, 잔여 인건비 예산을 비교하여 과소·과다 집행 여부를 점검합니다.</li>
          <li>필요 시 엑셀로 내보내 예산 편성·조정 자료로 활용할 수 있습니다.</li>
        </ul>
        <p><strong>▸ 주휴수당 예산관리</strong> (weekly-allowance)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>사업별로 주휴수당 발생 기준(사업 종료월, 주당 발생시간, 시간당 단가, 월 지급횟수)을 설정하여 향후 필요 예산을 추정합니다.</li>
          <li>급여 입력 기준월(asOfMonth)까지의 사용 예산과, 종료월까지 예상되는 향후 예산을 구분해 볼 수 있습니다.</li>
          <li>사업별 설정값을 조정하면 예상 주휴수당 예산이 어떻게 변하는지 시뮬레이션할 수 있습니다.</li>
        </ul>
      </>
    ),
  },
  {
    title: '급여·명세 관리',
    content: (
      <>
        <p><strong>▸ 참여자 급여 관리</strong> (payroll/register)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연도·월·사업을 선택하여 해당 월의 참여자 급여 목록을 조회합니다.</li>
          <li>
            <strong>관리자</strong>는 화면에서 급여를 수정·선택 삭제·엑셀 일괄 불러오기를 할 수 있고,{' '}
            <strong>일반 사용자</strong>는 조회만 가능합니다. 검증이 모두 통과한 뒤 같은 엑셀로 급여를 넣으려면{' '}
            <strong>급여 검증 및 입력</strong> 화면의 안내를 따르세요.
          </li>
          <li>
            관리자가 급여 항목을 수정하면 상태가 자동으로 발송준비로 바뀌며, 명세표 보기 또는 발송 시 최신 내용으로
            PDF가 재생성됩니다.
          </li>
        </ul>
        <p><strong>▸ 자동이체 자료 출력</strong> (payroll/auto-transfer)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연도·월·사업단·성명으로 급여를 조회하고, 선택한 사업단의 자동이체 엑셀(인건비·주휴수당·시간외수당·연차수당·기타수당·개인공제)을 내려받습니다.</li>
        </ul>
        <p><strong>▸ 급여 명세표 발송</strong> (payroll/send)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>발송 준비/발송 성공/발송 실패 등 상태별로 참여자 명단을 조회합니다.</li>
          <li>PDF 명세표 미리보기, 개별·일괄 이메일 발송을 수행합니다.</li>
          <li>발송 실패 건은 재시도하거나 오류 메시지를 확인하여 수정 후 다시 발송할 수 있습니다.</li>
        </ul>
        <p><strong>▸ 이메일 관리함</strong> (mailbox)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>이메일 설정(SMTP/IMAP)에 연결된 메일함에서 수신·발신 메일을 조회합니다.</li>
          <li>명세표 발송 시 사용하는 이메일 주소와 연동됩니다.</li>
        </ul>
        <p><strong>▸ 사회보험 입력</strong> (insurance/employment, <strong>관리자 전용</strong>)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>고용·건강·요양·산재 보험료를 엑셀로 불러와 반영합니다. 일반 사용자는 메뉴·화면·API 모두 이용할 수 없습니다.</li>
        </ul>
        <p><strong>▸ 급여자료 출력</strong> (insurance/export)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>급여·사회보험·근무·연차를 한 장표로 조회·엑셀 다운로드합니다.</li>
        </ul>
      </>
    ),
  },
  {
    title: '기본 설정',
    content: (
      <>
        <p><strong>▸ 사업 등록</strong> (project-settings, 관리자 전용)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연도별 사업단(프로젝트) 정보를 등록·수정합니다. 일일근무시간, 월 만근기준, 월 연차발생시간, 연차수당 단가 등이 연차 발생·정산에 사용됩니다. 사업별 은행명·사용계좌·예금주와, 상단 공통 설정의 4대보험 예수금 통장은 급여 자동이체 엑셀에 사용됩니다.</li>
        </ul>
        <p><strong>▸ 급여 명세표 설정</strong> (payroll/settings, 관리자 전용)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>명세표 서식, 도장 이미지, 폰트 등을 설정합니다. PDF 명세표 생성에 반영됩니다.</li>
        </ul>
        <p><strong>▸ 이메일 설정</strong> (payroll/email-settings, 관리자 전용)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>SMTP(발송)·IMAP(수신) 서버, 발신자 정보, 명세표 메일 제목·본문 템플릿을 설정합니다.</li>
          <li>설정이 잘못되면 명세표 발송 및 메일함 조회가 실패하므로, 설정 후 테스트 발송으로 확인하는 것이 좋습니다.</li>
        </ul>
        <p><strong>▸ 개인 설정</strong> (personal-settings)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>비밀번호 변경, 기본 사업단 선택, 연차 승인·취소·반려 시 알림 수신 여부 등을 설정합니다.</li>
        </ul>
        <p><strong>▸ 관리자 설정</strong> (admin-settings, 관리자 전용)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>시스템 사용자 계정과 권한(일반/관리자)을 관리합니다.</li>
        </ul>
        <p><strong>▸ 전산데이터 정리</strong> (admin-data-cleanup, 관리자 전용)</p>
        <ul style={{ margin: '6px 0 12px 20px', paddingLeft: 0 }}>
          <li>연차·근무 레코드 정리, 취소 건 연차 현황 보정, 기지급 이상 보정 등을 미리보기 후 실행할 수 있습니다.</li>
        </ul>
      </>
    ),
  },
  {
    title: '공통 주의사항',
    content: (
      <>
        <p>· 이 시스템에서 등록·수정한 모든 정보는 급여·연차 정산 및 명세표 발송에 직접 사용됩니다. 저장 전에 입력값을 반드시 다시 한 번 확인해 주세요.</p>
        <p>· 근무·연차 기록이 잘못되면 연차 정산과 급여(연차수당)에 영향을 주므로, 월 마감 전에 월별 합계와 연차 현황을 꼼꼼히 점검하세요.</p>
        <p>· 사업단 정보, 급여명세표 설정, 이메일 설정은 전체 기능에 영향을 주므로, 변경 후 테스트를 통해 정상 동작을 확인하는 것이 좋습니다.</p>
      </>
    ),
  },
];

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  versionText: {
    fontSize: '0.9rem',
    color: '#64748b',
    fontWeight: 500,
  },
  quickMenuSection: {
    marginBottom: '32px',
  },
  quickMenuTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '16px',
  },
  quickMenuGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '12px',
  },
  quickMenuCard: {
    background: '#ffffff',
    padding: '16px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
    border: '1px solid #e5e7eb',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    transition: 'all 0.2s ease',
  },
  quickMenuCardHover: {
    borderColor: '#2563eb',
    boxShadow: '0 4px 12px rgba(37,99,235,0.15)',
  },
  quickMenuIcon: {
    color: '#2563eb',
  },
  quickMenuLabel: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#374151',
    textAlign: 'center',
  },
  guideSection: {
    marginTop: '24px',
  },
  guideTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: '#007bff',
    borderBottom: '1px solid #eee',
    paddingBottom: '12px',
    marginTop: 0,
    marginBottom: '20px',
  },
  guideItem: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '12px',
    overflow: 'hidden',
  },
  guideItemHeader: {
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    background: '#f8fafc',
    fontWeight: 600,
    color: '#334155',
    fontSize: '0.95rem',
  },
  guideItemBody: {
    padding: '16px 18px',
    borderTop: '1px solid #e5e7eb',
    fontSize: '0.9rem',
    lineHeight: 1.7,
    color: '#475569',
  },
  copyrightFooter: {
    marginTop: '32px',
    padding: '16px 20px',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.85rem',
    color: '#64748b',
    lineHeight: 1.6,
    textAlign: 'center',
  },
  copyrightLink: {
    color: '#2563eb',
    textDecoration: 'none',
    fontWeight: 500,
  },
};

const DASHBOARD_NOTICE_STORAGE_KEY = 'dashboard-notice-dismissed';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expandedGuide, setExpandedGuide] = useState(null);
  const [notice, setNotice] = useState({ enabled: false, title: '', content: '', attachments: [], updatedAt: null });
  const [noticeModalOpen, setNoticeModalOpen] = useState(false);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewObjectUrl, setPreviewObjectUrl] = useState('');
  const [previewPdfData, setPreviewPdfData] = useState(null);
  const [previewPdfPage, setPreviewPdfPage] = useState(1);
  const [previewPdfTotalPages, setPreviewPdfTotalPages] = useState(0);
  const [previewPdfScale, setPreviewPdfScale] = useState(1);
  const [previewImageScale, setPreviewImageScale] = useState(1);
  const [previewContainerWidth, setPreviewContainerWidth] = useState(0);
  const previewUrlRef = useRef('');
  const previewContainerRef = useRef(null);

  useEffect(() => {
    api.get('/dashboard/notice')
      .then((res) => {
        const data = res.data;
        const noticeData = {
          enabled: !!data.enabled,
          title: data.title || '',
          content: data.content || '',
          attachments: Array.isArray(data.attachments) ? data.attachments : [],
          updatedAt: data.updatedAt || null
        };
        setNotice(noticeData);
        if (data.enabled && data.title) {
          const stored = sessionStorage.getItem(DASHBOARD_NOTICE_STORAGE_KEY);
          const currentVersion = noticeData.updatedAt || (noticeData.title + noticeData.content);
          const isDismissed = stored === currentVersion;
          setNoticeDismissed(isDismissed);
          if (!isDismissed) setNoticeModalOpen(true);
        }
      })
      .catch(() => {});
  }, []);

  const handleNoticeClose = (dontShowAgain) => {
    setNoticeModalOpen(false);
    if (dontShowAgain) {
      const version = notice.updatedAt || (notice.title + notice.content);
      sessionStorage.setItem(DASHBOARD_NOTICE_STORAGE_KEY, version);
      setNoticeDismissed(true);
    }
  };

  const handleAttachmentClick = (att) => {
    setPreviewFile(att);
    setPreviewOpen(true);
    setPreviewError('');
    setPreviewObjectUrl('');
    setPreviewPdfData(null);
    setPreviewPdfPage(1);
    setPreviewPdfTotalPages(0);
    setPreviewImageScale(1);
  };

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
  }, []);

  const fetchPreview = useCallback(async () => {
    if (!previewFile) return;
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const token = sessionStorage.getItem('token');
      const res = await api.get(`/dashboard/notice/files/${encodeURIComponent(previewFile.filename)}`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      revokePreviewUrl();
      const blob = res.data;
      const isPdf = previewFile.filename.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        setPreviewPdfData(blob);
        setPreviewPdfTotalPages(0);
        setPreviewPdfPage(1);
        setPreviewObjectUrl('');
        previewUrlRef.current = '';
      } else {
        const objectUrl = URL.createObjectURL(blob);
        previewUrlRef.current = objectUrl;
        setPreviewObjectUrl(objectUrl);
        setPreviewPdfData(null);
        setPreviewPdfTotalPages(0);
      }
    } catch (err) {
      setPreviewError(err.response?.data?.message || '파일을 불러올 수 없습니다.');
      setPreviewObjectUrl('');
      setPreviewPdfData(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [previewFile, revokePreviewUrl]);

  useEffect(() => {
    if (previewOpen && previewFile) fetchPreview();
  }, [previewOpen, previewFile, fetchPreview]);

  useEffect(() => () => revokePreviewUrl(), [revokePreviewUrl]);

  useEffect(() => {
    if (!previewOpen || !previewContainerRef.current) return;
    setPreviewContainerWidth(previewContainerRef.current.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) setPreviewContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(previewContainerRef.current);
    return () => observer.disconnect();
  }, [previewOpen]);

  const handlePdfDocumentLoad = ({ numPages }) => {
    setPreviewPdfTotalPages(numPages || 1);
    setPreviewPdfPage(1);
  };

  const handlePdfDocumentError = (error) => {
    console.error('PDF preview load error:', error);
    setPreviewError(error?.message || 'PDF 파일을 불러오는 중 오류가 발생했습니다.');
  };

  const handlePdfPageChange = (delta) => {
    setPreviewPdfPage((prev) => {
      const next = prev + delta;
      if (next < 1 || next > previewPdfTotalPages) return prev;
      return next;
    });
  };

  const handlePdfZoom = (delta) => {
    setPreviewPdfScale((prev) => {
      const next = Math.min(2.5, Math.max(0.6, prev + delta));
      return Number(next.toFixed(2));
    });
  };

  const handleImageZoom = (delta) => {
    setPreviewImageScale((prev) => {
      const next = Math.min(3, Math.max(0.5, prev + delta));
      return Number(next.toFixed(2));
    });
  };

  const handleClosePreview = () => {
    revokePreviewUrl();
    setPreviewOpen(false);
    setPreviewFile(null);
    setPreviewObjectUrl('');
    setPreviewPdfData(null);
    setPreviewImageScale(1);
  };

  const isPdfPreview = previewFile?.filename?.toLowerCase()?.endsWith('.pdf');
  const basePdfWidth = Math.max(previewContainerWidth, 320);
  const effectivePdfWidth = basePdfWidth * previewPdfScale;

  const handleGoTo = (path) => {
    navigate(path);
  };

  const toggleGuide = (index) => {
    setExpandedGuide((prev) => (prev === index ? -1 : index));
  };

  const noticeOverlay = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };
  const noticeModalBox = {
    background: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '85vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  };
  const noticeModalBody = { padding: '24px', overflow: 'auto', flex: 1 };
  const noticeModalFooter = { padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' };

  return (
    <div>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={{ margin: 0 }}>대시보드</h2>
          <span style={styles.versionText}>senior_work_portal 버전: v 4.0</span>
        </div>
        {user?.role === 'admin' && (
          <div style={styles.headerRight}>
            <Link
              to="/admin-settings#notice"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                background: '#e2e8f0',
                color: '#334155',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                border: '1px solid #cbd5e1',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#cbd5e1';
                e.currentTarget.style.borderColor = '#94a3b8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#e2e8f0';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
            >
              <Settings size={16} />
              공지 설정 바로가기
            </Link>
          </div>
        )}
      </div>

      {/* 자주가는 매뉴 */}
      <div style={styles.quickMenuSection}>
        <h3 style={styles.quickMenuTitle}>자주가는 매뉴</h3>
        <div style={styles.quickMenuGrid}>
          {QUICK_MENUS.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.path}
                style={styles.quickMenuCard}
                onClick={() => handleGoTo(item.path)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37,99,235,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.06)';
                }}
              >
                <Icon size={28} style={styles.quickMenuIcon} />
                <span style={styles.quickMenuLabel}>{item.label}</span>
              </div>
            );
          })}
          <div
            style={styles.quickMenuCard}
            onClick={() => handleGoTo('/copyright')}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#2563eb';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(37,99,235,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.06)';
            }}
          >
            <Scale size={28} style={styles.quickMenuIcon} />
            <span style={styles.quickMenuLabel}>저작권 및 이용 안내</span>
          </div>
        </div>
      </div>

      {/* 시스템 사용안내 */}
      <div style={styles.guideSection}>
        <h3 style={styles.guideTitle}>💡 시스템 사용 안내</h3>
        {GUIDE_SECTIONS.map((section, index) => (
          <div key={index} style={styles.guideItem}>
            <div style={styles.guideItemHeader} onClick={() => toggleGuide(index)}>
              <span>{section.title}</span>
              {expandedGuide === index ? (
                <ChevronDown size={20} color="#64748b" />
              ) : (
                <ChevronRight size={20} color="#64748b" />
              )}
            </div>
            {expandedGuide === index && (
              <div style={styles.guideItemBody}>
                {section.content}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={styles.copyrightFooter}>
        <p style={{ margin: '0 0 8px 0' }}>{COPYRIGHT_SHORT_LINE}</p>
        <Link to="/copyright" style={styles.copyrightLink}>저작권 및 이용 안내 전문 보기</Link>
      </div>

      {/* 대시보드 공지 모달 */}
      {noticeModalOpen && notice.enabled && notice.title && (
        <div style={noticeOverlay} onClick={() => handleNoticeClose(false)}>
          <div style={noticeModalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{notice.title}</h3>
              <button type="button" onClick={() => handleNoticeClose(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={noticeModalBody}>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, lineHeight: 1.6, fontSize: '0.95rem' }}>{notice.content || '(내용 없음)'}</pre>
              {notice.attachments && notice.attachments.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontWeight: 500, marginBottom: '8px' }}>첨부파일</div>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {notice.attachments.map((a) => (
                      <li key={a.filename} style={{ marginBottom: '4px' }}>
                        <button
                          type="button"
                          onClick={() => handleAttachmentClick(a)}
                          style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontSize: 'inherit' }}
                        >
                          {a.originalname || a.filename}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div style={noticeModalFooter}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                <input type="checkbox" id="notice-dismiss" />
                <span>다시 보지 않기</span>
              </label>
              <button
                type="button"
                onClick={() => {
                  const dontShow = document.getElementById('notice-dismiss')?.checked;
                  handleNoticeClose(dontShow);
                }}
                style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 첨부파일 미리보기 모달 */}
      {previewOpen && previewFile && (
        <div style={noticeOverlay} onClick={handleClosePreview}>
          <div
            style={{ ...noticeModalBox, maxWidth: '900px', maxHeight: '90vh', minHeight: '60vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>{previewFile.originalname || previewFile.filename}</span>
              <button type="button" onClick={handleClosePreview} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            {(isPdfPreview && previewPdfData) || (!isPdfPreview && previewObjectUrl) ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid #e5e7eb', gap: 12, flexWrap: 'wrap' }}>
                {isPdfPreview && previewPdfData && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => handlePdfPageChange(-1)}
                    disabled={previewPdfPage <= 1}
                    style={{ padding: '6px 12px', border: '1px solid #ced4da', borderRadius: 4, background: '#fff', cursor: previewPdfPage <= 1 ? 'not-allowed' : 'pointer', opacity: previewPdfPage <= 1 ? 0.5 : 1 }}
                  >
                    이전
                  </button>
                  <span style={{ fontSize: '0.9rem', color: '#555' }}>{previewPdfPage} / {previewPdfTotalPages || 1}</span>
                  <button
                    type="button"
                    onClick={() => handlePdfPageChange(1)}
                    disabled={previewPdfPage >= previewPdfTotalPages}
                    style={{ padding: '6px 12px', border: '1px solid #ced4da', borderRadius: 4, background: '#fff', cursor: previewPdfPage >= previewPdfTotalPages ? 'not-allowed' : 'pointer', opacity: previewPdfPage >= previewPdfTotalPages ? 0.5 : 1 }}
                  >
                    다음
                  </button>
                </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: isPdfPreview ? 0 : 'auto' }}>
                  <button
                    type="button"
                    onClick={() => isPdfPreview ? handlePdfZoom(-0.1) : handleImageZoom(-0.2)}
                    disabled={isPdfPreview ? previewPdfScale <= 0.6 : previewImageScale <= 0.5}
                    style={{ padding: '6px 12px', border: '1px solid #ced4da', borderRadius: 4, background: '#fff', cursor: (isPdfPreview ? previewPdfScale <= 0.6 : previewImageScale <= 0.5) ? 'not-allowed' : 'pointer' }}
                  >
                    축소
                  </button>
                  <span style={{ fontSize: '0.9rem', color: '#555', minWidth: 48 }}>
                    {Math.round((isPdfPreview ? previewPdfScale : previewImageScale) * 100)}%
                  </span>
                  <button
                    type="button"
                    onClick={() => isPdfPreview ? handlePdfZoom(0.1) : handleImageZoom(0.2)}
                    disabled={isPdfPreview ? previewPdfScale >= 2.5 : previewImageScale >= 3}
                    style={{ padding: '6px 12px', border: '1px solid #ced4da', borderRadius: 4, background: '#fff', cursor: (isPdfPreview ? previewPdfScale >= 2.5 : previewImageScale >= 3) ? 'not-allowed' : 'pointer' }}
                  >
                    확대
                  </button>
                  <button
                    type="button"
                    onClick={() => isPdfPreview ? setPreviewPdfScale(1) : setPreviewImageScale(1)}
                    style={{ padding: '6px 12px', border: '1px solid #ced4da', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
                  >
                    100%
                  </button>
                </div>
              </div>
            ) : null}
            <div
              ref={previewContainerRef}
              style={{
                flex: 1,
                overflow: 'auto',
                padding: 20,
                display: 'flex',
                alignItems: isPdfPreview ? 'flex-start' : 'center',
                justifyContent: isPdfPreview ? 'flex-start' : 'center',
                minHeight: 300,
                background: '#f6f7f9',
                borderRadius: 6,
                margin: 16,
                marginTop: 0
              }}
            >
              {previewLoading && <p>파일을 불러오는 중입니다...</p>}
              {previewError && !previewLoading && <p style={{ color: 'red' }}>{previewError}</p>}
              {!previewLoading && !previewError && previewObjectUrl && (
                <div style={{ overflow: 'auto', width: '100%', flex: 1, minHeight: 280 }}>
                  <img
                    src={previewObjectUrl}
                    alt="미리보기"
                    style={{
                      width: `${100 * previewImageScale}%`,
                      height: 'auto',
                      display: 'block',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              )}
              {!previewLoading && !previewError && !previewObjectUrl && isPdfPreview && previewPdfData && (
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <Document
                    file={previewPdfData}
                    onLoadSuccess={handlePdfDocumentLoad}
                    onLoadError={handlePdfDocumentError}
                    loading="PDF 로딩 중..."
                    error="PDF 로딩 실패"
                    noData="표시할 PDF가 없습니다."
                  >
                    <Page
                      pageNumber={previewPdfPage}
                      width={effectivePdfWidth}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />
                  </Document>
                </div>
              )}
              {!previewLoading && !previewError && !previewObjectUrl && (!isPdfPreview || !previewPdfData) && (
                <p>파일을 불러올 수 없습니다.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
